import { useEffect } from "react"
import { useNavigate, useParams } from "react-router-dom"

import { Skeleton, Tabs, type TabsProps } from "@/atoms"
import { NAMES_ROUTES } from "@/const/routes.constants"
import {
  isAiDiscoveryEnabled,
  isAiDiscoveryMock,
  isFeatureFlagMaybe
} from "@/domain/user"
import { isSelfHosted } from "@/helpers/self-hosted.helper"
import { useFeatureFlags } from "@/hooks/useUser"
import {
  AgentCoreColor,
  AKS,
  AzureApps,
  CopilotStudioColor,
  EC2,
  ECS,
  EKS,
  Fargate,
  FoundryColor,
  GKE,
  Kubernetes,
  Lambda,
  OpenShift,
  VM
} from "@/icons"
import { CopilotStudioConnection } from "@/pages/DeploymentsPage/components/organisms/CopilotStudioConnection"
import {
  AI_DEPLOY_TYPES,
  ALL_SUPPORTED_DEPLOY_TYPES,
  DEFAULT_DEPLOY_TAB,
  SELF_HOSTED_SUPPORTED_DEPLOY_TYPES,
  TYPE_DEPLOY
} from "../../../const"
import { DeployTypeButton } from "../../molecules/DeployTypeButton"
import { AgentCoreTab } from "../../organisms/AgentCoreTab"
import { AzureAppsTab } from "../../organisms/AzureAppsTab"
import { CommonDeployTab } from "../../organisms/CommonDeployTab"
import { EC2Tab } from "../../organisms/EC2Tab"
import { EcsTab } from "../../organisms/EcsTab"
import { FoundryTab } from "../../organisms/FoundryTab"
import { LambdaTab } from "../../organisms/LambdaTab"
import { VmTab } from "../../organisms/VmTab"

import styles from "./DeployTabs.module.scss"

import type { DeployTabsProps } from "."
import { EksTab } from "../EksTab"

export const DeployTabs = ({ loading, tabProps }: DeployTabsProps) => {
  const navigate = useNavigate()
  const { type } = useParams()
  // The agent platforms, with their cloud connections, are offered wherever
  // the AI Agents page is shown, live or demo
  const aiDiscovery = useFeatureFlags("aiDiscovery")
  const isAiAllowed = isAiDiscoveryEnabled(aiDiscovery)
  // Self-hosted offers no agent platform, so it doesn't wait for the flag
  const isAiLoading = !isSelfHosted() && isFeatureFlagMaybe(aiDiscovery)

  const supportedDeployTypes = (
    isSelfHosted()
      ? SELF_HOSTED_SUPPORTED_DEPLOY_TYPES
      : ALL_SUPPORTED_DEPLOY_TYPES
  ).filter((key) => isAiAllowed || !AI_DEPLOY_TYPES.includes(key))

  const allItems = [
    {
      label: <DeployTypeButton name="Kubernetes" Icon={Kubernetes} />,
      key: TYPE_DEPLOY.KUBERNETES,
      children: <CommonDeployTab {...tabProps} />
    },
    {
      label: <DeployTypeButton name="AKS" Icon={AKS} />,
      key: TYPE_DEPLOY.AKS,
      children: <CommonDeployTab {...tabProps} />
    },
    {
      label: <DeployTypeButton name="EKS" Icon={EKS} />,
      key: TYPE_DEPLOY.EKS,
      children: <CommonDeployTab {...tabProps} />
    },
    {
      label: <DeployTypeButton name="EKS Fargate" Icon={Fargate} />,
      key: TYPE_DEPLOY.EKS_FARGATE,
      children: <EksTab fargate {...tabProps} />
    },
    {
      label: <DeployTypeButton name="GKE" Icon={GKE} />,
      key: TYPE_DEPLOY.GKE,
      children: <CommonDeployTab {...tabProps} />
    },
    {
      label: <DeployTypeButton name="GKE Autopilot" Icon={GKE} />,
      key: TYPE_DEPLOY.GKE_AUTOPILOT,
      children: <CommonDeployTab autopilot {...tabProps} />
    },
    {
      label: <DeployTypeButton name="OpenShift" Icon={OpenShift} />,
      key: TYPE_DEPLOY.OPENSHIFT,
      children: <CommonDeployTab {...tabProps} />
    },
    {
      label: <DeployTypeButton name="EC2" Icon={EC2} />,
      key: TYPE_DEPLOY.EC2,
      children: <EC2Tab {...tabProps} />
    },
    {
      label: <DeployTypeButton name="ECS" Icon={ECS} />,
      key: TYPE_DEPLOY.ECS,
      children: <EcsTab {...tabProps} />
    },
    {
      label: <DeployTypeButton name="ECS Fargate" Icon={ECS} />,
      key: TYPE_DEPLOY.ECS_FARGATE,
      children: <EcsTab fargate {...tabProps} />
    },
    {
      label: <DeployTypeButton name="AWS Lambda" Icon={Lambda} />,
      key: TYPE_DEPLOY.LAMBDA,
      children: <LambdaTab {...tabProps} />
    },
    {
      label: <DeployTypeButton name="VM" Icon={VM} />,
      key: TYPE_DEPLOY.VM,
      children: <VmTab {...tabProps} />
    },
    {
      label: <DeployTypeButton name="Azure Apps" Icon={AzureApps} />,
      key: TYPE_DEPLOY.AZURE_APPS,
      children: <AzureAppsTab {...tabProps} />
    },
    {
      label: <DeployTypeButton name="AgentCore" Icon={AgentCoreColor} />,
      key: TYPE_DEPLOY.AGENTCORE,
      children: <AgentCoreTab {...tabProps} />
    },
    {
      label: <DeployTypeButton name="Foundry" Icon={FoundryColor} />,
      key: TYPE_DEPLOY.FOUNDRY,
      children: (
        <FoundryTab
          {...tabProps}
          demoCloudConnection={isAiDiscoveryMock(aiDiscovery)}
        />
      )
    },
    {
      label: (
        <DeployTypeButton name="Copilot Studio" Icon={CopilotStudioColor} />
      ),
      key: TYPE_DEPLOY.COPILOT_STUDIO,
      children: <CopilotStudioConnection />
    }
  ]

  // Filter items to only show supported deployment types
  const items = allItems.filter((item) =>
    supportedDeployTypes.includes(
      item.key as (typeof supportedDeployTypes)[number]
    )
  ) as TabsProps["items"]

  useEffect(() => {
    // An agent platform's tab is known only once the flag has loaded
    if (isAiLoading) return

    // items holds only the deploy types supported here (self-hosted supports a
    // subset), so an unknown type and a known but unsupported one both redirect
    const isSupported = !!items?.some(
      (item) => item.key === type && !item.disabled
    )
    const shouldRedirectToDefaultTab = !isSupported

    const { ROOT, NEW_DEPLOYMENT } = NAMES_ROUTES.DEPLOYMENTS

    const url = shouldRedirectToDefaultTab
      ? `/${ROOT}/${NEW_DEPLOYMENT}/${DEFAULT_DEPLOY_TAB}`
      : "."

    navigate(url, { replace: true })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAiLoading])

  return (
    <Tabs
      className={styles.container}
      activeKey={type}
      items={
        loading || isAiLoading
          ? [
              {
                label: (
                  <Skeleton.Node active>
                    <div />
                  </Skeleton.Node>
                ),
                key: "loading",
                children: (
                  <>
                    <Skeleton active paragraph={{ rows: 3 }} />
                    <Skeleton active paragraph={{ rows: 3 }} />
                  </>
                )
              }
            ]
          : items
      }
      onChange={(key) => {
        const { ROOT, NEW_DEPLOYMENT } = NAMES_ROUTES.DEPLOYMENTS

        navigate(`/${ROOT}/${NEW_DEPLOYMENT}/${key}`)
      }}
    />
  )
}
